import { Autocomplete, useLoadScript } from '@react-google-maps/api'
import { useState } from 'react'
import styled from 'styled-components'

import { rem } from '../../utils/theme'
import InputField from '../InputField'
import { getAddressString, processGooglePlaceAddress } from './common'
import EntryModeSelect from './EntryModeSelect'
import RenderMap from './RenderMap'

const StyledInlineMap = styled.div`
  width: 100%;
  padding-top: ${rem(10)};
`

const libraries = ['places']

function MapAndSearch(props) {
  const {
    id,
    apiKey,
    mainInputProps,
    addressValue,
    coordinates,
    enableSearch,
    enableMap,
    mapOptions,
    showEntryModeSelect,
    setEntryMode,
    groupAddressFields,
    handleOnChange,
    disabled,
    allowEmptyAutofill,
  } = props

  const { isMapInline = true } = mapOptions

  const [autocomplete, setAutocomplete] = useState(null)
  const [autocompleteValue, setAutocompleteValue] = useState(null)
  const [autocompleteError, setAutocompleteError] = useState(null)

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey,
    libraries,
    region: 'au',
  })

  if (!isLoaded) return null

  const handleAutocompleteLoaded = autocompleteInstance => {
    setAutocomplete(autocompleteInstance)
  }

  const handleOnPlaceChanged = () => {
    if (!autocomplete) return
    setAutocompleteError(null)

    const newPlace = autocomplete.getPlace()
    // biome-ignore lint/complexity/useOptionalChain: TODO(biome-2.4): newly detected in 2.4 upgrade
    if (!newPlace || !newPlace.geometry || !newPlace.geometry.location) {
      setAutocompleteError('Please select a valid address')
      setAutocompleteValue(getAddressString(addressValue))
      return
    }

    const newLat = newPlace.geometry.location.lat()
    const newLng = newPlace.geometry.location.lng()
    const { newAddress, addressParts } = processGooglePlaceAddress(newPlace)

    setAutocompleteValue(getAddressString(newAddress))

    handleOnChange(
      {
        newAddress,
        addressParts,
        newLat,
        newLng,
      },
      'place'
    )

    setAutocompleteValue(null)
  }

  const handleAutocompleteChange = e => {
    setAutocompleteValue(e.target.value)

    if (allowEmptyAutofill && e.target.value === '') {
      handleOnChange({
        newAddress: {
          country: null,
          postalCode: null,
          state: null,
          street: null,
          street2: null,
          suburb: null,
        },
        addressParts: null,
        newLat: null,
        newLng: null,
      })
    }
  }

  const mapProps = {
    id,
    isLoaded,
    loadError,
    coordinates,
    mapOptions,
  }

  const showMapInline = enableMap && isMapInline
  const showMapBelow = enableMap && !isMapInline
  const showMapOnlyInline = enableMap && !enableSearch && isMapInline
  const showMapOnlyBelow = enableMap && !enableSearch && !isMapInline

  return (
    <>
      {enableSearch && (
        <Autocomplete onLoad={handleAutocompleteLoaded} onPlaceChanged={handleOnPlaceChanged}>
          <InputField
            disabled={disabled}
            {...mainInputProps}
            id={`${id}-google-map`}
            value={autocompleteValue !== null ? autocompleteValue : getAddressString(addressValue)}
            onChange={handleAutocompleteChange}
            errorText={mainInputProps.errorText || autocompleteError}
            childrenBeforeInput={
              showEntryModeSelect &&
              groupAddressFields && (
                <EntryModeSelect
                  entryMode="search"
                  setEntryMode={setEntryMode}
                  groupAddressFields={groupAddressFields}
                  id={id}
                  disabled={disabled}
                />
              )
            }
            childrenAfterInput={
              showMapInline && (
                <StyledInlineMap>
                  <RenderMap {...mapProps} />
                </StyledInlineMap>
              )
            }
          />
        </Autocomplete>
      )}
      {(showMapBelow || showMapOnlyBelow) && <RenderMap {...mapProps} />}
      {showMapOnlyInline && (
        <InputField disabled={disabled} {...mainInputProps}>
          <StyledInlineMap>
            <RenderMap {...mapProps} />
          </StyledInlineMap>
        </InputField>
      )}
    </>
  )
}

export default MapAndSearch
